// Tela de aparelhos: parear uma vez, e depois so olhar.
//
// Fica fora do monitor de proposito — o monitor e leitura pura, sem botao que
// alguem possa tocar por engano (docs/PRODUCT.md §6.1).
import type { StatusAparelho, TipoDeAparelho } from '../ble/coletor';
import './aparelhos.css';

const TEXTO_ESTADO: Record<StatusAparelho['estado'], string> = {
  desconhecido: 'não pareado',
  procurando: 'esperando o aparelho ligar',
  conectando: 'conectando',
  conectado: 'conectado',
  desconectado: 'fora do ar',
};

const quando = (ms?: number) => {
  if (!ms) return null;
  const min = Math.floor((Date.now() - ms) / 60_000);
  if (min < 1) return 'dado agora';
  if (min < 60) return `último dado há ${min} min`;
  return `último dado há ${Math.floor(min / 60)} h`;
};

interface Props {
  status: StatusAparelho[];
  naFila: number;
  suportado: boolean;
  lembraAparelhos: boolean;
  podeSimular: boolean;
  aoParear: (tipo: TipoDeAparelho) => void;
  aoDesligar: (tipo: TipoDeAparelho) => void;
  aoSimular: (tipo: TipoDeAparelho) => void;
  aoVoltar: () => void;
}

export function Aparelhos({
  status, naFila, suportado, lembraAparelhos, podeSimular,
  aoParear, aoDesligar, aoSimular, aoVoltar,
}: Props) {
  return (
    <main className="aparelhos">
      <header className="aparelhos__topo">
        <h1>Aparelhos</h1>
        <button className="botao botao--voltar" onClick={aoVoltar}>Voltar ao monitor</button>
      </header>

      {!suportado && (
        <p className="aviso-erro">
          Este navegador não lê Bluetooth. No tablet do quarto use o <b>Chrome no Android</b> —
          o Safari do iPhone não tem Web Bluetooth, e por isso não serve como ponte.
        </p>
      )}

      {suportado && !lembraAparelhos && (
        <p className="aparelhos__alerta">
          Este Chrome ainda esquece os aparelhos quando a página recarrega. Para o tablet
          reconectar sozinho de madrugada, ative em <code>chrome://flags</code> a opção
          <b> Use the new permissions backend for Web Bluetooth</b> e reinicie o navegador.
        </p>
      )}

      <div className="aparelhos__lista">
        {status.map((a) => (
          <article className={`aparelho aparelho--${a.estado}`} key={a.tipo}>
            <div className="aparelho__info">
              <b>{a.rotulo}</b>
              <span className="aparelho__estado">
                <i className="aparelho__ponto" />
                {TEXTO_ESTADO[a.estado]}
                {a.nome ? ` · ${a.nome}` : ''}
              </span>
              {quando(a.ultimoDadoEm) && <small>{quando(a.ultimoDadoEm)}</small>}
              {a.mensagem && <small className="aparelho__erro">{a.mensagem}</small>}
            </div>

            <div className="aparelho__acoes">
              {podeSimular && (
                <button
                  className="botao botao--fantasma aparelho__simular"
                  onClick={() => aoSimular(a.tipo)}
                  title="Injeta os bytes reais capturados na Etapa 0, pelo mesmo caminho de um aparelho de verdade"
                >
                  Simular medição
                </button>
              )}
              {a.estado === 'conectado' || a.estado === 'procurando' ? (
                <button className="botao botao--fantasma" onClick={() => aoDesligar(a.tipo)}>Esquecer</button>
              ) : (
                <button className="botao" disabled={!suportado} onClick={() => aoParear(a.tipo)}>
                  Parear
                </button>
              )}
            </div>
          </article>
        ))}
      </div>

      <footer className="aparelhos__rodape">
        <p>
          {naFila > 0
            ? `${naFila} ${naFila === 1 ? 'medida guardada' : 'medidas guardadas'} para enviar quando a rede voltar.`
            : 'Nenhuma medida esperando envio.'}
        </p>
        <p className="aparelhos__nota">
          Pareie uma vez por aparelho. Depois disso o tablet reconecta sozinho quando o
          aparelho é ligado — não precisa tocar na tela de madrugada.
        </p>
        <p className="aparelhos__nota">
          <b>No glicosímetro:</b> insira a fita, espere o card ficar verde e só então
          aplique a gota. Ele deixa de ser visível assim que começa a medir, e uma
          medição perdida custa uma fita. O monitor de pressão não precisa disso — ele
          fica visível durante toda a inflação.
        </p>
        {podeSimular && (
          <p className="aparelhos__nota aparelhos__nota--dev">
            <b>Modo desenvolvimento:</b> “Simular medição” injeta os bytes reais capturados
            dos aparelhos, pelo mesmo caminho de uma medida de verdade. Não existe fora do
            desenvolvimento.
          </p>
        )}
      </footer>
    </main>
  );
}
